const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/** Cloudflare dummy sitekey — always passes, works on any hostname. */
export const TURNSTILE_TEST_SITE_KEY = '1x00000000000000000000AA';

/**
 * Hosts that should use Cloudflare's always-pass test site key.
 * Production Turnstile hostname allowlists only cover signup.bildit.co /
 * roitool.web.app — never these non-prod Firebase or custom domains.
 */
const TURNSTILE_TEST_HOSTS = [
  'localhost',
  '127.0.0.1',
  'dev-roi-tool',
  'dev.signup.bildit.co',
  'stage-roi-tool',
  'stage.signup.bildit.co',
];

/**
 * Firebase Hosting preview channels, e.g. roitool--onboarding-dydbtue7.web.app.
 * These are NOT subdomains of roitool.web.app, so production Turnstile hostnames
 * never cover them — use the Cloudflare always-pass test key instead.
 * @param {string} [hostname]
 * @returns {boolean}
 */
export function isFirebaseHostingPreviewHost(hostname) {
  const host =
    hostname ||
    (typeof window !== 'undefined' ? window.location.hostname : '');
  if (!host) {
    return false;
  }
  return (
    /^[a-z0-9-]+--[a-z0-9-]+\.web\.app$/i.test(host) ||
    /^[a-z0-9-]+--[a-z0-9-]+\.firebaseapp\.com$/i.test(host)
  );
}

/**
 * True when this hostname should render Cloudflare's always-pass test widget.
 * @param {string} [hostname]
 * @returns {boolean}
 */
export function isTurnstileTestHost(hostname) {
  const host =
    hostname ||
    (typeof window !== 'undefined' ? window.location.hostname : '');
  if (!host) {
    return false;
  }
  if (isLocalhostDevHost(host) || isFirebaseHostingPreviewHost(host)) {
    return true;
  }
  return TURNSTILE_TEST_HOSTS.some((pattern) => host.includes(pattern));
}

/**
 * CRA dev server on localhost / 127.0.0.1 only (not dev-roi-tool or preview channels).
 * @param {string} [hostname]
 * @returns {boolean}
 */
export function isLocalhostDevHost(hostname) {
  const host =
    hostname ||
    (typeof window !== 'undefined' ? window.location.hostname : '');
  return host === 'localhost' || host === '127.0.0.1';
}

function resolveTurnstileSiteKey() {
  const configured = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
  if (isTurnstileTestHost()) {
    if (!configured) {
      return TURNSTILE_TEST_SITE_KEY;
    }
    if (
      !configured.startsWith('1x') &&
      !configured.startsWith('2x') &&
      !configured.startsWith('3x')
    ) {
      return TURNSTILE_TEST_SITE_KEY;
    }
  }
  return configured;
}

export const turnstileSiteKey = resolveTurnstileSiteKey();

export function isTurnstileEnabled() {
  // Local yarn start: Functions emulator skips verification without a token.
  // The Turnstile widget is often blocked by browser extensions on localhost.
  if (isLocalhostDevHost()) {
    return false;
  }
  return Boolean(turnstileSiteKey);
}

/**
 * Logs Turnstile configuration/runtime errors for developers only.
 * @param {string|number|undefined} errorCode
 */
export function logTurnstileError(errorCode) {
  const code = String(errorCode ?? '');
  const host =
    typeof window !== 'undefined' ? window.location.hostname : 'this domain';

  let message;
  if (code === '400020' || code === '110100' || code === '110110') {
    message =
      `Security check configuration error (invalid site key) on ${host}. ` +
      'In Cloudflare Turnstile, copy the Site Key into REACT_APP_TURNSTILE_SITE_KEY ' +
      '(not the Secret Key). Add hostnames: signup.bildit.co, roitool.web.app, bildit.co.';
  } else if (code === '110200') {
    message =
      `Security check configuration error (domain not authorized) on ${host}. ` +
      'Dev, staging, and preview channels use the Cloudflare test site key automatically. ' +
      'For other hosts, add the hostname under Hostname Management in Cloudflare Turnstile.';
  } else if (code === '400070') {
    message =
      'Security check configuration error: Turnstile widget is disabled in Cloudflare.';
  } else if (code === 'load_failed') {
    message = 'Failed to load the Turnstile script.';
  } else {
    message = `Turnstile error: ${code || 'unknown'}`;
  }

  console.error('[Turnstile]', message, { errorCode: code, hostname: host });
}

/** Generic message safe to show end users when Turnstile blocks signup. */
export const TURNSTILE_USER_ERROR_MESSAGE =
  'Security verification failed. Please try again or refresh the page.';

let scriptLoadPromise = null;

export function loadTurnstileScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Turnstile requires a browser environment'));
  }
  if (window.turnstile) {
    return Promise.resolve();
  }
  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load Turnstile'))
      );
      return;
    }

    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Turnstile'));
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}
